import _ from 'lodash';
import { createSelector } from 'reselect';
import { getFeaturedVideoId } from '@wix/wix-vod-shared/dist/src/widget/ui-selectors/selected-video-id';
import { canShowCancelSubscription, canShowSubscription } from './subscription';
import { getMainVideo } from './get-video';
import {
  isVideoPlayingOptimistic,
  isVideoPausedOptimistic,
} from './video-playback-status';
import {
  canShowChannelShare,
  getCanShowChannelInfo,
  getCanShowSignIn,
  canShowChannelTitle,
  isVerticalLayoutMobile,
} from './app-settings';
import { canShowChannelCategories } from './categories';
import { getOverlayContent } from './player';
import { VideoSources } from '../redux/types';
import INFO_OVERLAY_CONTENT from '../player-widget/components/new-player/containers/animated-overlay-content/info-overlay-content-enums';
import {
  VERTICAL_LAYOUT_HEADER_HEIGHT,
  VERTICAL_LAYOUT_VIDEO_HEIGHT,
  VERTICAL_LAYOUT_VIDEO_MARGIN_BOTTOM,
  VERTICAL_LAYOUT_LOAD_MORE_BUTTON_HEIGHT,
  DEFAULT_WIDGET_HEIGHT,
  DEFAULT_WIDGET_WIDTH,
} from '../constants/sizes';
import {
  getChannelVideos,
  getCurrentPageIndex,
  getVideosPerPageCount,
} from '../redux/lazy-channel-videos/selectors';
import * as viewModeSelectors from './view-mode';
import { isPortableDevice } from './form-factor';

export const canShowActionBar = createSelector(
  canShowChannelTitle,
  getCanShowSignIn,
  getCanShowChannelInfo,
  canShowChannelShare,
  canShowCancelSubscription,
  canShowSubscription,
  canShowChannelCategories,
  (...args) => _.some(args),
);

export const showAutoPlay = createSelector(
  getMainVideo,
  isPortableDevice,
  (mainVideo, isPortableDevice) =>
    !!mainVideo &&
    !(mainVideo.videoSource === VideoSources.Facebook && isPortableDevice) &&
    !(mainVideo.videoSource === VideoSources.Vimeo && isPortableDevice),
);

export const isAllCardsOverlayVisible = createSelector(
  getOverlayContent,
  (overlayContent) => overlayContent === INFO_OVERLAY_CONTENT.ALL_CARDS,
);

export const isPlayerActive = createSelector(
  isAllCardsOverlayVisible,
  isVideoPlayingOptimistic,
  isVideoPausedOptimistic,
  (isAllCardsOverlayVisible, isVideoPlaying, isVideoPaused) =>
    isAllCardsOverlayVisible || isVideoPlaying || isVideoPaused,
);

export const getVerticalVisibleVideos = createSelector(
  getCurrentPageIndex,
  getChannelVideos,
  getFeaturedVideoId,
  getVideosPerPageCount,
  (currentPageIndex, channelVideos, featuredVideoId, videosPerPage) => {
    if (featuredVideoId) {
      const featuredVideoIndex = _.findIndex(
        channelVideos,
        ({ id }) => id === featuredVideoId,
      );
      const featuredVideo = channelVideos[featuredVideoIndex];
      channelVideos.splice(featuredVideoIndex, 1);
      channelVideos.unshift(featuredVideo);
    }

    return _.slice(channelVideos, 0, (currentPageIndex + 1) * videosPerPage);
  },
);

export const getWidgetSize = createSelector(
  isVerticalLayoutMobile,
  getVerticalVisibleVideos,
  (state) => state.windowSize,
  viewModeSelectors.isSiteMode,
  (isVertical, verticalVisibleVideos, windowSize, isSite) => {
    if (isVertical) {
      const visibleVideosCount = verticalVisibleVideos.length;
      const width = DEFAULT_WIDGET_WIDTH;
      let height = DEFAULT_WIDGET_HEIGHT;

      if (visibleVideosCount > 0) {
        height =
          VERTICAL_LAYOUT_HEADER_HEIGHT +
          visibleVideosCount *
            (VERTICAL_LAYOUT_VIDEO_HEIGHT +
              VERTICAL_LAYOUT_VIDEO_MARGIN_BOTTOM) +
          VERTICAL_LAYOUT_LOAD_MORE_BUTTON_HEIGHT;
      }

      return {
        height,
        width,
      };
    }

    // Previously users had a possibility to resize widget height. Now widget calculates height by itself.
    // But some users had already developed site layouts based on old widget height.
    // So, we do not touch widget height in view mode unless user will not try to change it in edit mode,
    // then widget will recalculate height.
    if (isSite) {
      const { height, width } = windowSize;
      return {
        height: Math.max(height, DEFAULT_WIDGET_HEIGHT),
        width,
      };
    }

    return {
      width: DEFAULT_WIDGET_WIDTH,
      height: DEFAULT_WIDGET_HEIGHT,
    };
  },
);

export const getShareModalTopPosition = (videoIndex) =>
  VERTICAL_LAYOUT_HEADER_HEIGHT +
  videoIndex *
    (VERTICAL_LAYOUT_VIDEO_HEIGHT + VERTICAL_LAYOUT_VIDEO_MARGIN_BOTTOM) +
  VERTICAL_LAYOUT_VIDEO_HEIGHT / 2;
